/**
 * catalog-client.js
 * Queries pycsw OGC API - Records to discover available layers
 * that have been published to pygeoapi, plus external web service
 * records (hierarchyLevel="service") published to the same catalog.
 */

const PYCSW_BASE = '/collections/metadata:main/items';

/**
 * Walk a GeoJSON geometry and return its [west, south, east, north]
 * envelope. pycsw's record2json (ogc/api/records.py:1662) emits the
 * record's spatial extent as the geometry polygon but does *not* set
 * a top-level `bbox` field — without this fallback, downstream code
 * (e.g. legend "Zoom to Extent") never sees an extent for catalog
 * records. Mirrors the helper in secims_view/js/bbox-thumbnail.js so
 * the two callers behave consistently.
 */
function _bboxFromGeometry(geom) {
  if (!geom || !geom.type) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const visit = (c) => {
    const x = Number(c[0]), y = Number(c[1]);
    if (!isFinite(x) || !isFinite(y)) return;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  const walk = (arr, depth) => {
    if (!Array.isArray(arr)) return;
    if (depth === 0) { visit(arr); return; }
    for (const sub of arr) walk(sub, depth - 1);
  };
  switch (geom.type) {
    case 'Point':           walk(geom.coordinates, 0); break;
    case 'MultiPoint':      walk(geom.coordinates, 1); break;
    case 'LineString':      walk(geom.coordinates, 1); break;
    case 'MultiLineString': walk(geom.coordinates, 2); break;
    case 'Polygon':         walk(geom.coordinates, 2); break;
    case 'MultiPolygon':    walk(geom.coordinates, 3); break;
    default: return null;
  }
  if (!isFinite(minX) || !isFinite(maxX)) return null;
  return [minX, minY, maxX, maxY];
}

function _resolveFeatureBbox(feature) {
  if (Array.isArray(feature.bbox) && feature.bbox.length >= 4) {
    const nums = feature.bbox.slice(0, 4).map(Number);
    if (nums.every(isFinite)) return nums;
  }
  return _bboxFromGeometry(feature.geometry);
}

/**
 * Pick the attribution name for the map's dynamic attribution control.
 * Mirrors the ISO 19115 XPath
 *   gmd:identificationInfo/gmd:MD_DataIdentification/gmd:pointOfContact
 *   /gmd:CI_ResponsibleParty/gmd:organisationName/gco:CharacterString
 * pycsw flattens this into `properties.contacts`, where the dataset-scoped
 * entry carries `contacttype === 'pointOfContact'`. When the org name on
 * that contact is empty we fall back to the individual name on the same
 * contact — but never to the metadata record's `gmd:contact`, which
 * identifies the record author, not the data publisher.
 */
function _resolveAttributionOrg(feature) {
  const contacts = (feature.properties && feature.properties.contacts) || [];
  const datasetContact = contacts.find(c => c && c.contacttype === 'pointOfContact');
  if (!datasetContact) return null;
  const org = (datasetContact.organization || '').trim();
  if (org) return org;
  const individual = (datasetContact.name || '').trim();
  return individual || null;
}

// Protocols recognised on records with type=service. Anything else
// surfaced on those records is ignored — the legend can't render it,
// so silently dropping is better than producing dead toggles.
const SUPPORTED_SERVICE_PROTOCOLS = new Set([
  'OGC:WMS',
  'OGC:WMTS',
  'XYZ Tiles',
  'ESRI Rest Services'
]);

/**
 * Fetch all catalog records that have OGC API service URLs.
 * @returns {Promise<Array>} Array of layer descriptor objects
 */
export async function fetchCatalogLayers() {
  try {
    // Fetch all spatial dataset records (type=dataset)
    const response = await fetch(
      `${PYCSW_BASE}?f=json&limit=100&type=dataset`,
      { headers: {cache: 'no-store', 'Accept': 'application/geo+json' } }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    return data.features
      .filter(f => _hasServiceUrl(f))
      .flatMap(f => _parseRecord(f));
  } catch (error) {
    console.error('Failed to fetch catalog layers:', error);
    return [];
  }
}

/**
 * Fetch catalog records that represent external web services
 * (ISO 19115 hierarchyLevel="service"). Each record may carry multiple
 * service endpoints (e.g. the same dataset published as WMS and WMTS);
 * the parser returns one descriptor per supported link so the caller
 * can group/pick by record id.
 *
 * @returns {Promise<Array>} Array of service descriptor objects:
 *   { id, title, description, bbox, metadataUrl, serviceUrl, protocol,
 *     layerName }
 */
export async function fetchCatalogServices() {
  try {
    const response = await fetch(
      `${PYCSW_BASE}?f=json&limit=100&type=service`,
      { headers: {cache: 'no-store',  'Accept': 'application/geo+json' } }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    return (data.features || []).flatMap(f => _parseServiceRecord(f));
  } catch (error) {
    console.error('Failed to fetch catalog services:', error);
    return [];
  }
}

/**
 * Check if a record has any OGC API data-serving links. A record with
 * only a styles link (and no Features/Tiles/Coverages) has nothing
 * to render — skip it rather than emit an empty layer descriptor.
 */
function _hasServiceUrl(feature) {
  const links = feature.links || [];
  return links.some(l =>
    l.protocol && l.protocol.startsWith('OGC:API') &&
    l.protocol !== 'OGC:API - Styles'
  );
}

/**
 * Parse a catalog record into one or more layer descriptors.
 * A single record may have multiple service links (Features + Tiles),
 * so this returns an array.
 *
 * If the record carries a `rel=stylesheet` link (OGC API - Styles),
 * its URL is surfaced as `styleUrl` on every descriptor for the
 * record. The link is record-scoped so all descriptors share it.
 */
function _parseRecord(feature) {
  const props = feature.properties || {};
  const links = feature.links || [];
  const layers = [];

  // Find all OGC API service links
  const serviceLinks = links.filter(l =>
    // Treat data-serving APIs (Features/Tiles/Coverages) separately
    // from the styles API — the latter is metadata about how to render,
    // not a layer source. Without this guard the stylesheet entry would
    // create a phantom legend item.
    l.protocol && l.protocol.startsWith('OGC:API') &&
    l.protocol !== 'OGC:API - Styles'
  );

  // pycsw serves links with `href` in the OGC API - Records JSON
  // response (we wrote them as `url` server-side, but jsonify_links +
  // record2json renames it). Defensive read covers both.
  const stylesheetLink = links.find(l =>
    l && l.rel === 'stylesheet'
  );
  const styleUrl = stylesheetLink
    ? (stylesheetLink.href || stylesheetLink.url || null)
    : null;

  const attributionOrg = _resolveAttributionOrg(feature);

  for (const link of serviceLinks) {
    const protocol = link.protocol;
    if (!protocol) continue;

    const srt = props.spatialRepresentationType;
    layers.push({
      id: feature.id,
      title: props.title || 'Untitled',
      description: props.description || '',
      bbox: _resolveFeatureBbox(feature),
      serviceUrl: link.href,
      protocol: protocol,
      spatialRepType: Array.isArray(srt) ? srt[0] : (srt || 'vector'),
      metadataUrl: `${PYCSW_BASE}/${feature.id}?f=json`,
      styleUrl: styleUrl,
      attributionOrg: attributionOrg
    });
  }

  return layers;
}

/**
 * Parse a type=service record into one descriptor per supported link.
 * The metadata editor stores each external endpoint as a separate link
 * with its protocol code in `link.protocol` and the layer/name in
 * `link.name` (see secims_metadata/metadata-editor/js/state.js:112 and
 * json-to-state.js:157). We pass `layerName` through so the WMS factory
 * can use it as the LAYERS parameter — WMS service URLs typically don't
 * embed the layer identifier.
 */
function _parseServiceRecord(feature) {
  const props = feature.properties || {};
  const links = feature.links || [];
  const out = [];
  const attributionOrg = _resolveAttributionOrg(feature);

  for (const link of links) {
    if (!link || !link.protocol) continue;
    if (!SUPPORTED_SERVICE_PROTOCOLS.has(link.protocol)) continue;

    const url = link.href || link.url || '';
    if (!url) continue;

    out.push({
      id: feature.id,
      title: props.title || 'Untitled',
      description: props.description || '',
      bbox: _resolveFeatureBbox(feature),
      serviceUrl: url,
      protocol: link.protocol,
      // WMS/WMTS need the layer identifier; XYZ/ArcGIS REST don't.
      // Empty string is fine — the factory falls back to its own logic.
      layerName: link.name || link.title || '',
      metadataUrl: `${PYCSW_BASE}/${feature.id}?f=json`,
      attributionOrg: attributionOrg
    });
  }

  return out;
}
